import React, { useLayoutEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useOpen } from 'src/hooks/use-open';
import { getTestId } from 'src/helpers/test-helpers';

import Icons from 'assets/icons';

import { Stack } from 'ui/stack';

interface CollapsibleFiltersSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleFiltersSection = ({
  title,
  defaultOpen = false,
  children,
}: CollapsibleFiltersSectionProps) => {
  const { isOpen, handleToggle } = useOpen(defaultOpen);
  const [height, setHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, children]);

  const dataTestId = getTestId({
    prefix: 'collapsible-filter-section',
    label: title,
    suffix: 'btn',
  });

  return (
    <Stack sx={{ gap: 1.5, width: '100%' }}>
      <button
        type="button"
        className="flex items-center w-full cursor-pointer"
        onClick={handleToggle}
        data-testid={dataTestId}
      >
        <span className="flex-1 text-left text-sm font-semibold text-gray-700">
          {title}
        </span>
        <Icons.ChevronRight
          className={twMerge(
            'w-4 h-4 text-gray-700 transition-transform',
            isOpen && 'rotate-90'
          )}
        />
      </button>
      <div
        ref={contentRef}
        inert={!isOpen}
        className="-mx-1 overflow-hidden px-1 transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: isOpen ? `${height}px` : '0px' }}
      >
        <Stack sx={{ gap: 1.5, pb: 1.5 }}>{children}</Stack>
      </div>
    </Stack>
  );
};

export default CollapsibleFiltersSection;
